import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, Rocket, ShieldCheck, Eye } from 'lucide-react';

/**
 * Demo welcome modal — appears once per session for users with is_demo=true,
 * inviting them to start a free 7-day trial for full feature access.
 */
export default function DemoCtaModal() {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user?.is_demo) return;
    if (sessionStorage.getItem('fmflexipro_demo_cta_seen') === '1') return;
    const t = setTimeout(() => setOpen(true), 1500);
    return () => clearTimeout(t);
  }, [user]);

  const dismiss = () => {
    sessionStorage.setItem('fmflexipro_demo_cta_seen', '1');
    setOpen(false);
  };

  const startTrial = () => {
    dismiss();
    const redirect = `${window.location.origin}/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  if (!user?.is_demo) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent data-testid="demo-cta-modal" className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-amber-500" />
            <DialogTitle>You are viewing a read-only Demo</DialogTitle>
          </div>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-foreground">
            The demo shows sample data so you can explore. To <strong>create real buildings, raise complaints, dispatch work orders, and use AI insights</strong> for your own portfolio, start your <strong>free 7-day trial</strong>.
          </p>
          <ul className="space-y-2 pt-1">
            <li className="flex items-start gap-2">
              <Rocket className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
              <span>Full features for 7 days - no credit card required</span>
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />
              <span>Your own isolated company workspace - data stays private</span>
            </li>
            <li className="flex items-start gap-2">
              <Eye className="w-4 h-4 mt-0.5 text-sky-500 shrink-0" />
              <span>Continue from demo to trial - same Google account works</span>
            </li>
          </ul>
        </div>
        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button variant="outline" onClick={dismiss} data-testid="demo-cta-dismiss">Keep exploring demo</Button>
          <Button data-testid="demo-cta-start-trial" onClick={startTrial} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
            Start 7-day Free Trial
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
